import React, { useEffect, useMemo, useState } from "react";

/**
 * usePagination — client-side pagination hook.
 * Persists page size in localStorage under `pg_size:${storageKey}`.
 * Auto-resets to page 1 when the source items list length changes.
 *
 * @param {Array} items       Full array of rows (already filtered).
 * @param {string} storageKey Unique key per list for size persistence.
 * @param {number} defaultSize Default page size (100 by default).
 */
export function usePagination(items, storageKey, defaultSize = 100) {
  const [pageSize, _setPageSize] = useState(() => {
    try {
      const raw = localStorage.getItem(`pg_size:${storageKey}`);
      const parsed = parseInt(raw, 10);
      if ([50, 100, 250].includes(parsed)) return parsed;
    } catch (_) { /* ignore */ }
    return defaultSize;
  });
  const [page, setPage] = useState(1);
  const total = Array.isArray(items) ? items.length : 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Clamp page when items shrink (e.g. filter narrows)
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const setPageSize = (n) => {
    _setPageSize(n);
    try { localStorage.setItem(`pg_size:${storageKey}`, String(n)); } catch (_) { /* ignore */ }
    setPage(1);
  };

  const paged = useMemo(() => {
    if (!Array.isArray(items)) return [];
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  return { page, setPage, pageSize, setPageSize, totalPages, total, paged, storageKey };
}

/**
 * ListPagination — renders the page-size selector + first/prev/next/last nav.
 * Hidden when total <= pageSize (single page, per user requirement).
 */
export function ListPagination({ pagination, testid = "list-pagination" }) {
  const { page, setPage, pageSize, setPageSize, totalPages, total } = pagination || {};
  if (!pagination) return null;
  if (total <= pageSize) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid={testid}>
      <div className="text-slate-600">
        <span className="font-mono font-semibold">{total}</span> kayıt · Sayfa{" "}
        <span className="font-mono font-semibold">{page}</span>/<span className="font-mono">{totalPages}</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-slate-500">Sayfa boyutu:</label>
        <select
          value={pageSize}
          onChange={e => setPageSize(parseInt(e.target.value, 10) || 100)}
          className="border rounded px-1 py-0.5 text-xs"
          data-testid={`${testid}-size`}
        >
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={250}>250</option>
        </select>
        <button
          className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={page <= 1}
          onClick={() => setPage(1)}
          data-testid={`${testid}-first`}
        >« İlk</button>
        <button
          className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={page <= 1}
          onClick={() => setPage(p => Math.max(1, p - 1))}
          data-testid={`${testid}-prev`}
        >‹ Önceki</button>
        <button
          className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={page >= totalPages}
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          data-testid={`${testid}-next`}
        >Sonraki ›</button>
        <button
          className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={page >= totalPages}
          onClick={() => setPage(totalPages)}
          data-testid={`${testid}-last`}
        >Son »</button>
      </div>
    </div>
  );
}

export default ListPagination;
